"use client"

import * as React from "react"
import Link from "next/link"
import Image from "next/image"
import { useSelectedLayoutSegment } from "next/navigation"

import { MainNavItem } from "@/types"
import { siteConfig } from "@/config/site"
import { cn } from "@/lib/utils"
import { Icons } from "@/components/icons"
import { MobileNav } from "@/components/mobile-nav"

interface MainNavProps {
  items?: MainNavItem[]
  children?: React.ReactNode
}

export function MainNav({ items, children }: MainNavProps) {
  const segment = useSelectedLayoutSegment()
  const [showMobileMenu, setShowMobileMenu] = React.useState<boolean>(false)

  return (
    <div className="flex w-full items-center justify-between gap-4 md:w-auto md:justify-start md:gap-6">
      {/* Logo */}
      <Link
        href="/"
        className="group flex shrink-0 items-center gap-2 transition-opacity hover:opacity-80"
      >
        <Image
          src="/botbyte-logo.png"
          alt={siteConfig.name}
          width={280}
          height={140}
          className="h-16 w-auto object-contain transition-transform group-hover:scale-[1.02] sm:h-16 lg:h-25"
          priority
        />
      </Link>

      {/* Desktop nav links */}
      {items?.length ? (
        <div className="hidden items-center md:flex">
          <div className="mx-3 h-5 w-px bg-slate-200/80" />
          <nav className="flex items-center gap-1">
            {items.map((item, index) => {
              const isActive = item.href.startsWith(`/${segment}`)
              return (
                <Link
                  key={index}
                  href={item.disabled ? "#" : item.href}
                  className={cn(
                    "relative rounded-lg px-3 py-1.5 text-[13px] font-medium transition-all duration-200",
                    isActive
                      ? "bg-slate-100/80 text-slate-800"
                      : "text-slate-500 hover:bg-slate-50 hover:text-slate-700",
                    item.disabled && "cursor-not-allowed opacity-50"
                  )}
                >
                  {item.title}
                  {isActive && (
                    <span className="absolute bottom-0 left-1/2 h-0.5 w-4 -translate-x-1/2 rounded-full bg-indigo-500" />
                  )}
                </Link>
              )
            })}
          </nav>
        </div>
      ) : null}

      {/* Mobile hamburger */}
      <button
        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200/60 bg-white text-slate-500 shadow-sm transition-all hover:bg-slate-50 hover:text-slate-700 md:hidden"
        onClick={() => setShowMobileMenu(!showMobileMenu)}
        aria-label="Toggle menu"
      >
        {showMobileMenu ? (
          <Icons.close className="h-4 w-4" />
        ) : (
          <Icons.menu className="h-4 w-4" />
        )}
      </button>
      {showMobileMenu && items && (
        <MobileNav items={items} onClose={() => setShowMobileMenu(false)}>
          {children}
        </MobileNav>
      )}
    </div>
  )
}